import _ from "lodash";
import {MIN_DOCUMENTS_BANKS_NUMBER} from "./components/constants";
import {isIndividualAssetManagementBank, prepareDocumentsFilterData} from "../../../../../../utils";
import {GENERAL_DOCUMENTS_BANK_ID, GENERAL_DOCUMENTS_BANK_NAME, ONBOARDING_DEPOT_ID} from "./DocumentsDownload";
import {FormCenterHandlerResource} from "../../../../../../../../utils/api";
import {OBJECT_TYPES} from "../../../../../../constants";

export class DepotDocumentIdentifier {

  constructor(depotId, documentId, objectType) {
    this.__depotId = depotId
    this.__documentId = documentId
    this.__objectType = objectType
  }

  get documentId() {
    return this.__documentId
  }

  get depotId() {
    return this.__depotId
  }

  get objectType() {
    return this.__objectType
  }

  equals(_documentIdentifier) {
    return _documentIdentifier instanceof DepotDocumentIdentifier
      && this.depotId === _documentIdentifier.depotId
      && this.documentId === _documentIdentifier.documentId
      && this.objectType === _documentIdentifier.objectType
  }

}


export class DepotDocumentIdentifiersList extends Array{

  /**
   * @param searchElement {DepotDocumentIdentifier}
   */
  includes(searchElement, fromIndex) {

    return !!this.find((item) => item.equals(searchElement))

  }

  /**
   * Return new list and put document identifier to the end.
   * In case document identifier already in the list - remove it from there
   * @param documentIdentifier {DepotDocumentIdentifier} Document identifier
   * @returns {any[]|T[]}
   */
  toggleDocumentIdentifier(documentIdentifier) {

    // We always should return new list, so React could track change in list
    if (!this.includes(documentIdentifier)) {
      return super.concat(documentIdentifier)
    } else {
      return this.filter((_documentIdentifier) => !_documentIdentifier.equals(documentIdentifier))
    }

  }

  remove(documentIdentifier) {
    return this.filter((_documentIdentifier) => !_documentIdentifier.equals(documentIdentifier))
  }

  /**
   * Convert list of document identifiers to json structure
   * @returns {{[depotId: Number]: Number[]}}
   */
  toJSON() {

    const jsonRepresentation = {}
    this.forEach((documentIdentifier) => {

      _.defaults(jsonRepresentation, {[documentIdentifier.objectType]: {}})

      if (!jsonRepresentation[documentIdentifier.objectType].hasOwnProperty(documentIdentifier.depotId)) {
        jsonRepresentation[documentIdentifier.objectType][documentIdentifier.depotId] = []
      }

      jsonRepresentation[documentIdentifier.objectType][documentIdentifier.depotId]
        .push(documentIdentifier.documentId)

    })

    return jsonRepresentation

  }

  /**
   * Create list of depot documents identifiers from JSON structure
   * @param documentsIdentifiersJSON {{[p: Number]: (Number|String)[]}}
   * @returns {DepotDocumentIdentifiersList}
   */
  static fromJSON(documentsIdentifiersJSON) {

    const list = new DepotDocumentIdentifiersList()

    if (Array.isArray(documentsIdentifiersJSON)) return list;

    _.forIn(documentsIdentifiersJSON, (depotsDocuments, objectType) => {
      _.forIn(depotsDocuments, (documentsIds, depotId) => {
        (documentsIds || []).forEach((documentId) => {
          list.push(new DepotDocumentIdentifier(depotId, documentId, objectType))
        })
      })
    })

    return list

  }

}

/**
 * Check, if only one depot selected.
 * @param documentsData
 */
export function singleDepotSelected(documentsData) {
  if (Object.keys(documentsData).filter((objectType) => objectType != 'general').length > MIN_DOCUMENTS_BANKS_NUMBER) {
    return false
  }

  for (let objectType in documentsData) {
    if (Object.keys(documentsData[objectType]).length > MIN_DOCUMENTS_BANKS_NUMBER) {
      return false
    }
  }

  return true
}


/**
 * Async initialization for the "documents download" step.
 *
 * Fetches the bank documents, computes the default document selection, writes it
 * onto the step's `documents` answer and caches the prepared data on the service
 * for the DocumentsDownload component to render. Runs while the provider shows
 * the global loader, so the unsaved-changes baseline is captured against the
 * fully initialized (default-selected) state instead of an empty one.
 *
 * Idempotent: re-running recomputes defaults from a fresh fetch; safe on navigate-back re-activation.
 *
 * @param {Object} service - The active finalize/protocol service.
 * @return {Promise<void>}
 */
export const initDocumentsStep = async (service) => {
  const selectedDocuments = service.getQuestion('finalize-documents-downloading', 'documents');
  selectedDocuments.dataFetched = false;
  selectedDocuments.answer = {
    download: {},
    eSign: {},
    store: {}
  };
  delete selectedDocuments.documents;

  if (_.isFunction(service._validateMissingData) && !await service._validateMissingData()) {
    return;
  }

  await service._validateAccount();

  // We can call create-order end point many times.
  // Only one business case will be created with only one online document (latest created) inside.
  if(service.is_trading){
    await service.handleCreateOrderClick()
  } else if (typeof service._createAccountFunc === 'function'){
    await service._createAccountFunc();
  }

  const documents = await _prepareDocumentsData(service);
  if (!documents) {
    return;
  }

  const {docToDownload, docToSign, docToStore} = _getDefaultDocuments(documents);

  selectedDocuments.documents = {...documents};
  selectedDocuments.answer = {
    download: docToDownload.toJSON(),
    eSign: docToSign.toJSON(),
    store: docToStore.toJSON()
  };
  selectedDocuments.dataFetched = true;
};

const _prepareDocumentsData = async (service) => {
  const response = await _fetchDocuments(service);
  const selectedDocuments = service.getQuestion('finalize-documents-downloading', 'documents')

  const _documents = {}

  response.map(({depot, documents, objectType}) => {

    _.defaults(_documents, {[objectType]: {}})

    const bankData = _.find(_.isArray(service.bank)
      ? service.bank
      : Array(service.bank),
      (b) => b.custodian_id == depot.companyId) || {}

    const isOnline = documents.is_online;

    let requiredDocuments = documents.form_center.filter(d => d.is_mandatory)
    let optionalDocuments = documents.form_center.filter(d => !d.is_mandatory)

    if (isOnline) {
      selectedDocuments.documents = {
        ...selectedDocuments.documents,
        [depot.portfolioId]: [...requiredDocuments]
      }
    } else {
      requiredDocuments = [
        ...requiredDocuments,
        ...documents.legitimations,
        ...documents.proof_of_guardianship]
    }

    selectedDocuments.documents = {
      ...selectedDocuments.documents,
      [depot.portfolioId]: [...requiredDocuments]
    }

    const isIndividualAssetManagement = isIndividualAssetManagementBank(service, depot.companyId)
    let additionalInfo = bankData.document_selection_additional_info;
    if(isIndividualAssetManagement && service.isIndividualAssetManagementWithExistingDepot){
      additionalInfo = service.banksDataGrouped[depot.companyId].document_selection_info_for_existing_depot;
    }

    _documents[objectType][depot.portfolioId] = {
      required: requiredDocuments,
      allOptional: optionalDocuments,
      optional: documents.form_center.filter(d => d.to_optional_list),
      name: depot && depot.name || depot.data.name,
      onlineDocuments: documents.online || [],
      isOnline: isOnline,
      filterData: prepareDocumentsFilterData(optionalDocuments),
      additionalBankText: additionalInfo,
      isIndividualAssetManagement: isIndividualAssetManagement,
      general: documents.general,
      documentsToValidate: _.flatten([requiredDocuments, documents.general || []]) // list of all documents, that should be used for answer validation
    }

  })

  return _documents
}

const _fetchDocuments = async (service) => {
  if (service.is_trading) {
    /**
     * Fetch documents for specific depot.
     * @param depot {{data: {id: Number}}} Depot data
     * @returns {Promise<void>}
     * @private
     */
    const _fetchDepotDocuments = async (depot, objectType=GENERAL_DOCUMENTS_BANK_ID, withGeneralDocuments=true, generalDocumentsOnly=false) => {
      const depotId = _.get(depot, 'data.id')
      if (!depotId) {
        return undefined
      }

      const response = await FormCenterHandlerResource.getTradingDocuments(
        service.onboarding_uid, depotId, objectType, withGeneralDocuments, generalDocumentsOnly
      )

      return {
        documents: response,
        depot,
        objectType
      }
    }

    const GENERAL_DOCUMENTS_DEPOT = {
      portfolioId: GENERAL_DOCUMENTS_BANK_ID,
      data: {
        id: GENERAL_DOCUMENTS_BANK_ID
      },
      name: GENERAL_DOCUMENTS_BANK_NAME
    }

    const processes = {
      [OBJECT_TYPES.TRADING]: ['buy', 'sell', 'switch'],
      [OBJECT_TYPES.SAVINGS_PLAN]: ['savings_plan'],
      [OBJECT_TYPES.PAYOUT_PLAN]: ['payout_plan'],
      [OBJECT_TYPES.SWITCH_PLAN]: ['switch_plan'],
    }

    const _processTransactionsExists = (depot, processTransactionsKeys) => {
      return _.some(processTransactionsKeys, (transactionKey) => !_.isEmpty(depot.transactions[transactionKey]))
    }

    const singleDepotSelected = service.investedDepots.length === 1
    const singleProcessSelected = (() => {

      for (let depot of service.investedDepots) {

        const processTransactionsExistBooleans = Object.entries(processes).map(([objectType, transactionsKeys]) => {
          return _processTransactionsExists(depot, transactionsKeys)
        })

        if (_.filter(processTransactionsExistBooleans, (value) => value).length > 1) {
          return false
        }
      }

      return true
    })()

    let _promises = []
    // In case multiple business cases will be created - separate business
    // case for general documents should be created also
    if (!singleDepotSelected || !singleProcessSelected) {
      _promises.push(_fetchDepotDocuments(GENERAL_DOCUMENTS_DEPOT, undefined, true, true))
    }

    _promises = _promises.concat(_.flatten(Object.entries(processes).map(([objectType, transactionsKeys]) => {

      return _.flatten(service.investedDepots.map((depot) => {
        // In case depot does not have any transaction from specific
        // object type - don`t retrieve any document
        if (_.every(transactionsKeys, (transactionKey) => _.isEmpty(depot.transactions[transactionKey]))) {
          return []
        }

        return _fetchDepotDocuments(depot, objectType, singleDepotSelected && singleProcessSelected)

      }))
    })))

    return await Promise.all(_promises)
  }

  return [{
    depot: {...service.bank, portfolioId: ONBOARDING_DEPOT_ID, companyId: service.bank.custodian_id}, // We do not have depot in case of onboarding, so use bank instead
    documents: await FormCenterHandlerResource.getOnboardingDocuments(service.onboarding_uid),
    objectType: service.objectType || 'general'
  }];
}

const _getDefaultDocuments = (documentsData) => {
  const docToStore = new DepotDocumentIdentifiersList(); // all selected optional documents
  const docToDownload = new DepotDocumentIdentifiersList();
  const docToSign = new DepotDocumentIdentifiersList();

  for (let [objectType, depotDocuments] of Object.entries(documentsData)) {
    for (let [bankId, documents] of Object.entries(depotDocuments)) {
      ((documents.required || []).concat(documents.general || [])).forEach(document => {
        if (document.is_mandatory || document.is_default_checked) {
          docToDownload.push(new DepotDocumentIdentifier(bankId, document.id, objectType))
        }
        if (document.sign_possible) {
          docToSign.push(new DepotDocumentIdentifier(bankId, document.id, objectType))
        }
      });
      (documents.optional || [])
        .forEach(d => docToStore.push(new DepotDocumentIdentifier(bankId, d.id, objectType)));

      // all online documents goes to E-Sign
      (documents.onlineDocuments || [])
        .forEach(d => docToSign.push(new DepotDocumentIdentifier(bankId, d.external_id, objectType)));
    }
  }

  return {docToStore, docToDownload, docToSign}
}